import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  MessageSquare, 
  CheckCircle, 
  AlertCircle, 
  Settings,
  Save,
  TestTube,
  Loader2,
  Workflow
} from 'lucide-react';
import apiService from '../services/api';
import SlackWorkflowManager from './SlackWorkflowManager';

const SlackConnection = () => {
  const [slackConfig, setSlackConfig] = useState({
    webhook_url: '',
    enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    loadSlackConfig();
  }, []);

  const loadSlackConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const config = await apiService.request('/slack/config');
      setSlackConfig({
        webhook_url: config.webhook_url || '',
        enabled: config.enabled || false
      });
    } catch (error) {
      console.error('Error loading Slack configuration:', error);
      // If no config exists, use defaults
      setSlackConfig({
        webhook_url: '',
        enabled: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setTestResult(null);
    
    try {
      await apiService.request('/slack/config', {
        method: 'POST',
        body: JSON.stringify({
          webhook_url: slackConfig.webhook_url,
          enabled: slackConfig.enabled
        })
      });
      
      setTestResult({ success: true, message: 'Slack configuration saved successfully!' });
    } catch (error) {
      console.error('Error saving Slack configuration:', error);
      setError('Failed to save Slack configuration: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!slackConfig.webhook_url) {
      setError('Please enter a webhook URL first');
      return;
    }

    setTesting(true);
    setError(null);
    setTestResult(null);

    try {
      const result = await apiService.request('/slack/test', {
        method: 'POST',
        body: JSON.stringify({
          message: 'Test message from Bond Collective Operations Platform! 🎉'
        })
      });
      
      setTestResult({
        success: true,
        message: 'Test message sent to Slack successfully! Check your channel.'
      });
    } catch (error) {
      console.error('Error testing Slack webhook:', error);
      setTestResult({
        success: false,
        message: 'Failed to send test message: ' + error.message
      });
    } finally {
      setTesting(false);
    }
  };

  const isValidWebhookUrl = (url) => {
    return url && url.includes('hooks.slack.com/services/');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>Loading Slack configuration...</span>
      </div>
    );
  }

  return (
    <Tabs defaultValue="setup" className="w-full">
      <TabsList className="grid w-full grid-cols-2 mb-6">
        <TabsTrigger value="setup">Setup & Connection</TabsTrigger>
        <TabsTrigger value="workflows">
          <Workflow className="w-4 h-4 mr-2" />
          Notification Workflows
        </TabsTrigger>
      </TabsList>

      <TabsContent value="setup" className="space-y-6">
        {/* Connection Status */}
        <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <MessageSquare className="w-6 h-6 text-slate-600" />
          <div>
            <h3 className="font-medium">Slack Notifications</h3>
            <p className="text-sm text-gray-600">
              Get notified when deals are signed and other important events
            </p>
          </div>
        </div>
        <Badge className={slackConfig.enabled && slackConfig.webhook_url ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
          {slackConfig.enabled && slackConfig.webhook_url ? 'Connected' : 'Not Connected'}
        </Badge>
      </div>

      {/* Configuration Form */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="webhook_url">Slack Webhook URL</Label>
          <Input
            id="webhook_url"
            type="url"
            placeholder="https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXXXXXX"
            value={slackConfig.webhook_url}
            onChange={(e) => setSlackConfig({
              ...slackConfig, 
              webhook_url: e.target.value
            })}
            className="mt-1"
          />
          <p className="text-xs text-gray-500 mt-1">
            Get your webhook URL from Slack by creating an Incoming Webhook integration
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="enabled"
            checked={slackConfig.enabled}
            onChange={(e) => setSlackConfig({
              ...slackConfig,
              enabled: e.target.checked
            })}
            className="rounded"
          />
          <Label htmlFor="enabled">Enable Slack notifications</Label>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <Button 
          onClick={handleSave} 
          disabled={saving || !slackConfig.webhook_url}
          className="flex-1"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Configuration
            </>
          )}
        </Button>
        
        <Button 
          onClick={handleTest} 
          variant="outline"
          disabled={testing || !isValidWebhookUrl(slackConfig.webhook_url)}
        >
          {testing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Testing...
            </>
          ) : (
            <>
              <TestTube className="w-4 h-4 mr-2" />
              Test
            </>
          )}
        </Button>
      </div>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <Settings className="w-5 h-5 mr-2" />
            Setup Instructions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm space-y-2">
            <p className="font-medium">To set up Slack notifications:</p>
            <ol className="list-decimal list-inside space-y-1 text-gray-600">
              <li>Go to your Slack workspace</li>
              <li>Navigate to Apps & Integrations</li>
              <li>Search for "Incoming Webhooks" and install it</li>
              <li>Choose the channel where you want notifications</li>
              <li>Copy the webhook URL and paste it above</li>
              <li>Enable notifications and save</li>
            </ol>
            <p className="text-xs text-gray-500 mt-2">
              You'll receive notifications for: Deal agreements signed, New leads, Important updates
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Status Messages */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {testResult && (
        <div className={`flex items-center space-x-2 p-3 border rounded-lg ${
          testResult.success 
            ? 'bg-green-50 border-green-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          {testResult.success ? (
            <CheckCircle className="w-4 h-4 text-green-600" />
          ) : (
            <AlertCircle className="w-4 h-4 text-red-600" />
          )}
          <span className={`text-sm ${
            testResult.success ? 'text-green-700' : 'text-red-700'
          }`}>
            {testResult.message}
          </span>
        </div>
      )}
      </TabsContent>

      <TabsContent value="workflows">
        <SlackWorkflowManager />
      </TabsContent>
    </Tabs>
  );
};

export default SlackConnection;